import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AuthService } from '../../../core/auth/auth.service';
import { PhoneInputComponent } from '../../../shared/phone-input/phone-input';
import { GENERIC_ERROR } from '../../../core/http/read-error';

@Component({
  selector: 'app-complete-profile',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    PhoneInputComponent,
  ],
  templateUrl: './complete-profile.html',
  styleUrl: './complete-profile.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompleteProfileComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);

  protected readonly email = signal('');
  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal<string | null>(null);

  protected readonly form = this.fb.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    cellNumber: this.fb.control<string | null>(null),
    jobPosition: [''],
  });

  protected get firstNameControl() {
    return this.form.controls.firstName;
  }
  protected get lastNameControl() {
    return this.form.controls.lastName;
  }
  protected get cellNumberControl() {
    return this.form.controls.cellNumber;
  }

  ngOnInit(): void {
    const user = this.authService.currentUser();
    if (!user) {
      void this.router.navigate(['/auth/login']);
      return;
    }
    if (user.profile_completed_at) {
      void this.router.navigate(['/dashboard']);
      return;
    }
    this.email.set(user.email);
  }

  protected onSubmit(): void {
    this.form.markAllAsTouched();
    if (this.isLoading() || this.form.invalid) return;

    this.errorMessage.set(null);
    this.isLoading.set(true);

    const { firstName, lastName, cellNumber, jobPosition } = this.form.getRawValue();

    const payload: Parameters<typeof this.authService.completeProfile>[0] = {
      first_name: firstName!,
      last_name: lastName!,
    };
    if (cellNumber) payload.cell_number = cellNumber;
    if (jobPosition) payload.job_position = jobPosition;

    this.authService.completeProfile(payload).subscribe({
      next: () => {
        this.isLoading.set(false);
        void this.router.navigate(['/dashboard']);
      },
      error: (err: unknown) => {
        this.isLoading.set(false);
        if (err instanceof HttpErrorResponse) {
          const authErr = this.authService.getAuthError(err);
          if (authErr?.code === 'VALIDATION_ERROR' && err.error?.detail) {
            this.applyServerErrors(err.error.detail as Record<string, string | string[]>);
          } else {
            this.errorMessage.set(
              authErr?.detail ?? 'Could not save your profile. Please try again.',
            );
          }
        } else {
          this.errorMessage.set(GENERIC_ERROR);
        }
        this.cdr.markForCheck();
      },
    });
  }

  private applyServerErrors(errors: Record<string, string | string[]>): void {
    const fieldMap: Record<string, string> = {
      first_name: 'firstName',
      last_name: 'lastName',
      cell_number: 'cellNumber',
      job_position: 'jobPosition',
    };
    Object.entries(errors).forEach(([backendField, message]) => {
      const controlName = fieldMap[backendField] ?? backendField;
      const errorText = Array.isArray(message) ? message[0] : message;
      this.form.get(controlName)?.setErrors({ serverError: errorText });
    });
    this.cdr.markForCheck();
  }
}
